/**
 * @file label-print-request.ts
 * @description Сборка тела `POST /admin/wms/labels/print` из формы диалога
 *   (phase19.22, W17-5a/5b/5c) — чистая функция, проверяется тестом.
 *
 * Правила, которые легко сломать правкой диалога:
 *   - шаблон строки шлём только переопределённый — иначе его разрешит сервер
 *     (фасовка ?? вид ?? STANDARD), и клиент не спорит с ним;
 *   - «только штрих» на мини-этикетке сервер отклонит (422
 *     `code_mode_not_supported`) — мини-строкам шлём DataMatrix;
 *   - «начать с N» — только по шаблонам, которые реально идут в печать;
 *   - `receiptId`/`receiptItemId` — только у печати из приёмки;
 *   - `cutLines` (пунктир реза) уходит всегда явным `true/false`: это
 *     настройка рабочего места, а не склада — сервер её не угадает.
 */

import type { LabelPrintDialogItem, LabelPrintMode } from "./label-print-types";
import type {
  LabelCodeMode,
  LabelPrintInput,
  LabelStartAt,
  LabelTemplate,
} from "../../../lib/api-labels";

/** Источник печати по режиму диалога. */
const SOURCE: Record<LabelPrintMode, LabelPrintInput["source"]> = {
  receipt: "RECEIPT",
  stock: "STOCK",
  sku: "SKU",
};

/** Отмеченная строка формы — ровно то, что нужно запросу. */
export interface LabelRequestRow {
  item: LabelPrintDialogItem;
  qty: number;
  /** Шаблон, выбранный на эту печать; `null` — как у фасовки. */
  override: LabelTemplate | null;
  /** Итоговый шаблон строки (для правила мини-этикетки). */
  template: LabelTemplate;
}

interface Params {
  mode: LabelPrintMode;
  warehouseId: string;
  receiptId?: string;
  codeMode: LabelCodeMode;
  /** Линии реза — печать на обычной бумаге (настройка рабочего места). */
  cutLines: boolean;
  /** «Начать с N» из формы (может содержать шаблоны вне печати). */
  startAt: LabelStartAt;
  rows: LabelRequestRow[];
}

export function buildLabelPrintInput({ mode, warehouseId, receiptId, codeMode, cutLines, startAt, rows }: Params): LabelPrintInput {
  const used = new Set(rows.map((r) => r.template));
  return {
    source: SOURCE[mode],
    warehouseId,
    receiptId: mode === "receipt" ? (receiptId ?? null) : null,
    codeMode,
    cutLines,
    startAt: Object.fromEntries([...used].map((t) => [t, startAt[t] ?? 1])),
    items: rows.map((r) => ({
      skuId: r.item.skuId,
      lotId: r.item.lotId,
      ...(mode === "receipt" ? { receiptItemId: r.item.receiptItemId ?? null } : {}),
      quantity: r.qty,
      ...(r.override ? { template: r.override } : {}),
      ...(r.template === "MINI" && codeMode === "CODE128_ONLY" ? { codeMode: "DATAMATRIX_ONLY" as const } : {}),
    })),
  };
}
